import type { ApiContext } from "@hobbyshopify/backend-client";
import { type ErrorResponse, loadStripeTerminal, type Reader, type Terminal } from "@stripe/terminal-js";
import type { AuthContextProvider } from "trunk.js";
import type { BackendApi } from "../ops/api";

/**
 * Stripe Terminal, for taking payment in person from the merchant app.
 *
 * ## What this can and cannot do
 *
 * This is the **JS** Terminal SDK, which discovers `internet` readers only:
 * the shipped types say so outright (`DiscoveryConfig = InternetMethodConfiguration`).
 * Tap to Pay (the phone itself as the reader) exists only in the native SDKs,
 * so it is not reachable from a browser at all. In practice this drives a
 * countertop reader (BBPOS WisePOS E, Stripe S700) on the same account, and
 * `simulated: true` for development. The server contract behind it,
 * `/providers/stripe/pos/*`, is the same one a native Tap to Pay client would use.
 *
 * ## Lifetime
 *
 * `Terminal.create` may be called **once per page load**; the SDK holds reader
 * connection state internally and creating a second instance silently breaks
 * reconnection. Hence one instance of this service for the app, holding the
 * in-flight promise so two callers initialising concurrently share it.
 */
export class StripeTerminal {
  #terminal: Promise<Terminal> | null = null;
  #scope: { storeId: string; locationId: string | null } | null = null;

  constructor(readonly deps: { client: Pick<BackendApi, "stripe">; session: AuthContextProvider<ApiContext> }) {}

  /**
   * The SDK calls `onFetchConnectionToken` itself, on init, on reconnect and
   * whenever the last one expires, from outside any controller, so it needs the
   * session to mint a fresh caller each time.
   */
  get(storeId: string, locationId: string | null): Promise<Terminal> {
    // Read by every token fetch, so readers are discovered at the counter the app is working at now.
    this.#scope = { storeId, locationId };
    if (this.#terminal) return this.#terminal;

    const { client, session } = this.deps;
    this.#terminal = (async () => {
      const StripeTerminalSdk = await loadStripeTerminal();
      if (!StripeTerminalSdk) throw new Error("Stripe Terminal could not be loaded");
      return StripeTerminalSdk.create({
        onFetchConnectionToken: async () => {
          // Throwing is right here: the SDK has no other way to be told, and a
          // shop whose Stripe account cannot take money yet has no reader to drive.
          const scope = this.#scope ?? { storeId, locationId };
          const r = await client.stripe.connectionToken(
            await session.authorizeCall(),
            scope.storeId,
            scope.locationId ?? undefined,
          );
          if (!r.ok) throw r.errors[0];
          return r.value.secret;
        },
        onUnexpectedReaderDisconnect: () => {
          // The SDK reconnects on its own when the reader comes back.
          console.warn("[Terminal] reader disconnected unexpectedly");
        },
      });
    })();

    // Let the next attempt retry rather than caching a rejected promise.
    this.#terminal.catch(() => {
      this.#terminal = null;
    });
    return this.#terminal;
  }
}

export type ReaderInfo = {
  id: string;
  label: string;
  status: string;
  serialNumber: string;
};

export function toReaderInfo(reader: Reader): ReaderInfo {
  return {
    id: reader.id,
    label: reader.label || reader.id,
    status: reader.status ?? "unknown",
    serialNumber: reader.serial_number,
  };
}

/**
 * The SDK signals failure by resolving with `{ error }` rather than rejecting,
 * so every call site would otherwise need its own `if ("error" in …)` branch.
 */
export function unwrap<T>(result: T | ErrorResponse, fallback: string): Exclude<T, ErrorResponse> {
  const maybeError = (result as ErrorResponse | undefined)?.error;
  if (maybeError) {
    throw new Error(maybeError.message || fallback);
  }
  return result as Exclude<T, ErrorResponse>;
}
